import { Form, Input } from "antd";
import type { GetRef, InputRef, TableProps } from "antd";
import { createContext, useContext, useEffect, useRef } from "react";

type FormInstance<T> = GetRef<typeof Form<T>>;

const EditableContext = createContext<FormInstance<any> | null>(null);

export interface DetailItem {
  key: string;
  name: string | React.ReactNode;
  value: string | React.ReactNode;
  required?: boolean;
  maxLength?: number;
  editable?: boolean;
}

export type ColumnTypes = Exclude<TableProps<DetailItem>['columns'], undefined>;

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  dataIndex: keyof DetailItem;
  record: DetailItem;
  handleSave: (record: DetailItem) => void;
  hasValidationError?: boolean;
}

export const EditableRow: React.FC<any> = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  hasValidationError = false,
  ...restProps
}) => {
  const inputRef = useRef<InputRef>(null);
  const form = useContext(EditableContext)!;
  const hasInitialized = useRef(false);
  const isFocused = useRef(false);

  useEffect(() => {
    // Only set initial value once when component mounts and not currently focused
    if (editable && form && record && typeof record.value === 'string' && !hasInitialized.current && !isFocused.current) {
      form.setFieldsValue({
        [dataIndex]: record.value
      });
      hasInitialized.current = true;
    }
  }, [form, dataIndex, record?.value, editable]);

  const save = async () => {
    try {
      const values = await form.validateFields();
      handleSave({ ...record, ...values });
      isFocused.current = false;
    } catch (errInfo) {
      console.error('Save failed:', errInfo);
      isFocused.current = false;
    }
  };

  const handleFocus = () => {
    isFocused.current = true;
  };

  const handleBlur = () => {
    save();
  };

  let childNode = children;
  if (editable && record) {
    if (typeof record.key === 'string' && record.key.includes('-header-') && dataIndex === 'name') {
      childNode = children;
    } else {
      // Build validation rules
      const rules = [];
      
      // Add required rule if specified
      if (record.required) {
        rules.push({
          required: true,
          message: `กรุณากรอกข้อมูลให้ครบ`,
        });
      }
      
      // Add max length rule if specified
      if (record.maxLength) {
        rules.push({
          max: record.maxLength,
          message: `${record.name} ตัวอักษรเกินกำหนด (${record.maxLength} ตัว)`,
        });
      }

      childNode = (
        <Form.Item
          style={{ margin: 0 }}
          name={dataIndex}
          initialValue={typeof record.value === 'string' ? record.value : ''}
          rules={rules}
          validateStatus={hasValidationError ? 'error' : undefined}
          help={hasValidationError ? 'กรุณากรอกข้อมูลให้ครบ' : undefined}
        >
          {(typeof record.value === 'string') ? (
            record.maxLength ? (
              <Input.TextArea
                ref={inputRef}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onPressEnter={(e) => {
                  // Prevent save on Enter, allow new line
                  e.stopPropagation();
                }}
                maxLength={record.maxLength}
                status={hasValidationError ? 'error' : undefined}
                style={hasValidationError ? { borderColor: '#ff4d4f' } : {}}
                autoSize={{ minRows: 1, maxRows: 8 }}
              />
            ) : (
              <Input 
                ref={inputRef} 
                onFocus={handleFocus}
                onBlur={handleBlur}
                onPressEnter={save} 
                status={hasValidationError ? 'error' : undefined}
                style={hasValidationError ? { borderColor: '#ff4d4f' } : {}}
              />
            )
          ) : record.value}
        </Form.Item>
      );
    }
  }

  return <td {...restProps}>{childNode}</td>;
};

export default EditableCell;